import { useCallback } from 'react';
import { useAuth } from 'react-oidc-context';
import { useFullLocation } from '../routing';
import { User } from 'oidc-client-ts';

import { BRANDS } from '../../components/Itv/types';
import { APP_URLS } from '../../consts';
import { useUserContext } from '../../providers/UserProvider';

type SignInParams = {
  acrValues?: string;
  returnQueryParameters?: Record<string, string>;
  theme?: BRANDS;
  redirectUri?: string;
  extraQueryParams?: Record<string, string>;
};

export const useSignIn = (): ((params?: SignInParams) => Promise<void> | Promise<User>) => {
  const auth = useAuth();
  const fullLocation = useFullLocation();
  const { theme: userTheme } = useUserContext();

  return useCallback(
    (params?: SignInParams) => {
      const redirectLocation = params?.redirectUri ?? fullLocation;
      const url = new URL(redirectLocation);

      if (!params?.redirectUri && url.pathname.includes(APP_URLS.registration)) {
        url.pathname = APP_URLS.home;
      }

      const newParams = new URLSearchParams(url.search);

      if (params?.returnQueryParameters) {
        Object.entries(params?.returnQueryParameters).forEach(([key, value]) => {
          newParams.set(key, value);
        });
      }

      url.search = newParams.toString();
      const isPopup = !!params?.acrValues;
      const signupFunction = isPopup ? auth.signinPopup : auth.signinRedirect;
      const themeToUse = params?.theme ?? userTheme;
      const extraQueryParams = {
        ...params?.extraQueryParams,
        ...(themeToUse ? { theme: themeToUse } : {}),
      };

      return signupFunction.bind(auth)({
        redirect_uri: url.toString(),
        ...(Object.keys(extraQueryParams).length ? { extraQueryParams } : {}),
      });
    },
    [auth, fullLocation, userTheme]
  );
};
